/**
 * SceneBackground — storybook-style animated scene system.
 * Uses illustrated images as actual backgrounds, with weather/season overlays.
 * Buddy lives in these worlds, not on abstract UI gradients.
 */

import { memo, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// ── Mining particles sub-component ──
const MINING_SYMBOLS = ['⛏', '▪', '▫', '⬜', '⛏', '▪'];

function MiningParticles({ minerActivity }: { minerActivity: number }) {
  const count = minerActivity > 0.3 ? Math.floor(minerActivity * 6) : 0;
  const pulsing = minerActivity > 0.75;

  const particles = useMemo(() =>
    Array.from({ length: 6 }, (_, i) => ({
      id: i,
      x: 10 + (i * 41 + 17) % 80,
      size: 8 + (i % 3) * 2,
      speed: 8 + (i % 4) * 2,
      delay: (i * 1.2) % 6,
      symbol: MINING_SYMBOLS[i % MINING_SYMBOLS.length],
    })),
  []);

  if (count === 0) return null;

  return (
    <div className="absolute inset-0 pointer-events-none">
      {particles.slice(0, count).map(p => (
        <motion.div
          key={`mine-${p.id}`}
          className="absolute"
          style={{
            left: `${p.x}%`,
            bottom: '5%',
            fontSize: p.size,
            lineHeight: 1,
          }}
          initial={{ opacity: 0 }}
          animate={{
            y: [0, '-45vh'],
            opacity: pulsing
              ? [0, minerActivity * 0.25, minerActivity * 0.2, minerActivity * 0.25, 0]
              : [0, minerActivity * 0.25, minerActivity * 0.25, 0],
          }}
          transition={{
            duration: p.speed,
            repeat: Infinity,
            ease: 'linear',
            delay: p.delay,
          }}
        >
          {p.symbol}
        </motion.div>
      ))}
    </div>
  );
}

// Scene images
import sceneClearSunny from '@/assets/scene-clear-sunny.webp';
import sceneClearHot from '@/assets/scene-clear-hot.webp';
import sceneStormy from '@/assets/scene-stormy.webp';
import sceneStormyLightning from '@/assets/scene-stormy-lightning.webp';
import sceneWinterMild from '@/assets/scene-winter-mild.webp';
import sceneWinterDeep from '@/assets/scene-winter-deep.webp';

type WeatherType = 'clear' | 'breezy' | 'busy' | 'stormy' | 'fee_hurricane' | string;

interface SceneBackgroundProps {
  weather?: WeatherType;
  seasonState?: string;
  seasonIntensity?: number;
  heatLevel?: number;
  minerActivity?: number;
}

/**
 * Resolve which scene image to use based on weather + season.
 * Priority: season climate > weather state
 */
function resolveSceneImage(weather: string, seasonState: string, seasonIntensity: number, heatLevel: number): string {
  const isCold = seasonState === 'cold';
  const isHeat = seasonState === 'heat';
  const si = Math.min(1, Math.max(0, seasonIntensity));

  // Deep winter overrides everything
  if (isCold && si > 0.4) return sceneWinterDeep;
  if (isCold) return sceneWinterMild;

  // Weather-based
  if (weather === 'fee_hurricane') return sceneStormyLightning;
  if (weather === 'stormy') return sceneStormy;

  // Hot / heatwave
  if (isHeat && si > 0.2) return sceneClearHot;

  // Default clear/breezy/busy
  return sceneClearSunny;
}

/** Unique key for crossfade transitions */
function sceneKey(img: string): string {
  if (img === sceneClearSunny) return 'sunny';
  if (img === sceneClearHot) return 'hot';
  if (img === sceneStormy) return 'stormy';
  if (img === sceneStormyLightning) return 'hurricane';
  if (img === sceneWinterMild) return 'winter-mild';
  if (img === sceneWinterDeep) return 'winter-deep';
  return 'default';
}

function SceneBackgroundInner({
  weather = 'clear',
  seasonState = 'neutral',
  seasonIntensity = 0,
  heatLevel = 0,
  minerActivity = 0.5,
}: SceneBackgroundProps) {
  const activeImage = resolveSceneImage(weather, seasonState, seasonIntensity, heatLevel);
  const key = sceneKey(activeImage);

  const isStormy = weather === 'stormy' || weather === 'fee_hurricane';
  const isHurricane = weather === 'fee_hurricane';
  const isCold = seasonState === 'cold';
  const isHeat = seasonState === 'heat';
  const si = Math.min(1, Math.max(0, seasonIntensity));

  // Rain particle count — increases with heat/congestion during storms
  const rainCount = useMemo(() => {
    const safeHeat = Math.min(1, Math.max(0, heatLevel));
    if (isHurricane) return Math.floor(30 + safeHeat * 15);
    if (isStormy) return Math.floor(18 + safeHeat * 12);
    return 0;
  }, [isStormy, isHurricane, Math.round(heatLevel * 4)]);

  // Snow particle count
  const snowCount = useMemo(() => {
    if (!isCold || si < 0.1) return 0;
    return Math.floor(8 + si * 20);
  }, [isCold, si]);

  // Rain particles
  const rainDrops = useMemo(() =>
    Array.from({ length: rainCount }, (_, i) => ({
      id: i,
      x: 5 + (i * 37 + 13) % 90,
      delay: (i * 0.15) % 2,
      speed: 0.4 + (i % 5) * 0.1,
      opacity: 0.15 + (i % 3) * 0.08,
      length: 12 + (i % 4) * 6,
    })),
  [rainCount]);

  // Snow particles
  const snowFlakes = useMemo(() =>
    Array.from({ length: snowCount }, (_, i) => ({
      id: i,
      x: 5 + (i * 43 + 7) % 90,
      size: 2 + (i % 4) * 1.5,
      delay: (i * 0.4) % 6,
      speed: 5 + (i % 4) * 2,
      drift: (i % 2 === 0 ? 1 : -1) * (8 + (i % 3) * 5),
      opacity: 0.3 + (i % 3) * 0.15,
    })),
  [snowCount]);

  return (
    <div className="absolute inset-0 z-0 overflow-hidden">

      {/* ═══ LAYER 1: SCENE IMAGE — crossfading illustrated background ═══ */}
      <AnimatePresence mode="sync">
        <motion.div
          key={key}
          className="absolute inset-0"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 2.5, ease: 'easeInOut' }}
        >
          {/* Slow parallax drift for life */}
          <motion.div
            className="absolute inset-0"
            animate={{
              scale: [1.02, 1.04, 1.02],
              x: [0, -3, 0],
              y: [0, -2, 0],
            }}
            transition={{
              duration: 25,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          >
            <div
              className="absolute inset-0"
              style={{
                backgroundImage: `url(${activeImage})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center 80%',
                backgroundRepeat: 'no-repeat',
              }}
            />
          </motion.div>
        </motion.div>
      </AnimatePresence>

      {/* ═══ LAYER 2: SCENE TINT — darken/warm to match app ═══ */}
      <div
        className="absolute inset-0"
        style={{
          background: `linear-gradient(180deg, 
            hsl(var(--background) / 0.35) 0%, 
            hsl(var(--background) / 0.15) 30%,
            hsl(var(--background) / 0.1) 50%,
            hsl(var(--background) / 0.2) 75%,
            hsl(var(--background) / 0.55) 100%
          )`,
        }}
      />

      {/* ═══ LAYER 3: WEATHER OVERLAYS ═══ */}

      {/* Rain overlay — stormy/hurricane */}
      {rainCount > 0 && (
        <div className="absolute inset-0 pointer-events-none">
          {rainDrops.map(drop => (
            <motion.div
              key={`rain-${drop.id}`}
              className="absolute"
              style={{
                left: `${drop.x}%`,
                top: '-5%',
                width: 1,
                height: drop.length,
                background: `linear-gradient(180deg, transparent, hsl(200 40% 70% / ${drop.opacity}))`,
                borderRadius: 1,
              }}
              animate={{
                y: [0, '110vh'],
                opacity: [0, drop.opacity, drop.opacity, 0],
              }}
              transition={{
                duration: drop.speed,
                repeat: Infinity,
                ease: 'linear',
                delay: drop.delay,
              }}
            />
          ))}
        </div>
      )}

      {/* Lightning flash — hurricane only */}
      {isHurricane && (
        <motion.div
          className="absolute inset-0 pointer-events-none"
          animate={{
            opacity: [0, 0, 0.12, 0, 0, 0, 0.08, 0, 0, 0, 0, 0, 0, 0],
          }}
          transition={{
            duration: 6,
            repeat: Infinity,
            ease: 'linear',
          }}
          style={{
            background: 'hsl(220 30% 80% / 0.15)',
          }}
        />
      )}

      {/* Stormy sky pulse — darker breathing */}
      {isStormy && !isHurricane && (
        <motion.div
          className="absolute inset-0 pointer-events-none"
          animate={{
            opacity: [0, 0.04, 0],
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
          style={{
            background: 'hsl(220 25% 60% / 0.08)',
          }}
        />
      )}

      {/* Snow overlay — cold season */}
      {snowCount > 0 && (
        <div className="absolute inset-0 pointer-events-none">
          {snowFlakes.map(flake => (
            <motion.div
              key={`snow-${flake.id}`}
              className="absolute rounded-full"
              style={{
                left: `${flake.x}%`,
                top: '-3%',
                width: flake.size,
                height: flake.size,
                background: `hsl(0 0% 95% / ${flake.opacity})`,
                boxShadow: `0 0 ${flake.size}px hsl(0 0% 90% / ${flake.opacity * 0.5})`,
              }}
              animate={{
                y: [0, '110vh'],
                x: [0, flake.drift, 0, -flake.drift * 0.6, 0],
                opacity: [0, flake.opacity, flake.opacity, 0],
              }}
              transition={{
                duration: flake.speed,
                repeat: Infinity,
                ease: 'linear',
                delay: flake.delay,
              }}
            />
          ))}
        </div>
      )}

      {/* Heat shimmer — hot season */}
      {isHeat && si > 0.15 && (
        <motion.div
          className="absolute inset-0 pointer-events-none"
          animate={{
            opacity: [si * 0.02, si * 0.06, si * 0.02],
          }}
          transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
          style={{
            background: `radial-gradient(ellipse 50% 30% at 50% 35%, hsl(30 80% 55% / 0.06) 0%, transparent 70%)`,
          }}
        />
      )}

      {/* Water shimmer — clear/breezy only */}
      {!isStormy && !isCold && (
        <motion.div
          className="absolute bottom-0 inset-x-0 pointer-events-none"
          style={{ height: '35%' }}
          animate={{
            opacity: [0.02, 0.05, 0.02],
          }}
          transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
        >
          <div
            className="absolute inset-0"
            style={{
              background: `radial-gradient(ellipse 80% 50% at 50% 60%, hsl(195 60% 55% / 0.06) 0%, transparent 70%)`,
            }}
          />
        </motion.div>
      )}

      {/* ═══ LAYER 3b: MINING PARTICLES — pickaxe/block shapes ═══ */}
      <MiningParticles minerActivity={minerActivity} />

      {/* Low miner activity vignette */}
      {minerActivity < 0.3 && (
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            boxShadow: `inset 0 0 80px 30px hsl(var(--background) / ${0.08 * (1 - minerActivity / 0.3)})`,
          }}
        />
      )}

      {/* ═══ LAYER 4: VIGNETTE — subtle edge darkening only ═══ */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          boxShadow: `inset 0 0 120px 40px hsl(var(--background) / ${isStormy ? 0.35 : 0.2})`,
        }}
      />
    </div>
  );
}

const SceneBackground = memo(SceneBackgroundInner);
export default SceneBackground;
